// src/pages/sitegroup/SiteGroupCreatePage.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Alert,
  Breadcrumbs,
  Link,
  Button,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getCompanies } from '../../api/companyAPI';
import { createSiteGroup } from '../../api/siteGroupAPI';
import SiteGroupForm from '../../components/sitegroup/SiteGroupForm';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const SiteGroupCreatePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Get company ID from URL params if present
  const companyIdFromUrl = searchParams.get('company');
  
  const [selectedCompanyId, setSelectedCompanyId] = useState(companyIdFromUrl || '');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch companies for dropdown
  const { data: companies, isLoading: isLoadingCompanies, isError: isCompaniesError, error: companiesError } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getCompanies()
  });

  // Set default company ID when companies are loaded and no company ID is set
  useEffect(() => {
    if (companies && companies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(companies[0].CompanyId.toString());
    }
  }, [companies, selectedCompanyId]);

  // Create site group mutation
  const mutation = useMutation({
    mutationFn: createSiteGroup,
    onSuccess: (data) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['site-groups', parseInt(selectedCompanyId)] });
      
      setSnackbar({
        open: true,
        message: 'Site group created successfully!',
        severity: 'success'
      });
      
      // Navigate to site groups list after creation
      setTimeout(() => {
        navigate('/site-groups');
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error creating site group: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleCompanyChange = (event) => {
    setSelectedCompanyId(event.target.value);
  };

  const handleSubmit = async (data) => {
    if (!selectedCompanyId) {
      setSnackbar({
        open: true,
        message: 'Please select a company',
        severity: 'error'
      });
      return;
    }
    
    try {
      await mutation.mutateAsync(data);
    } catch (err) {
      // Error is handled by the mutation
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoadingCompanies) {
    return <LoadingSpinner />;
  }

  if (isCompaniesError) {
    return <ErrorAlert message={companiesError instanceof Error ? companiesError.message : 'Error loading companies'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4">Create New Site Group</Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to="/site-groups" 
          startIcon={<ArrowBackIcon />}
        >
          Back to Site Groups
        </Button>
      </Box>

      {/* Company Selection */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Select Company</Typography>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="company-select-label">Company</InputLabel>
          <Select
            labelId="company-select-label"
            value={selectedCompanyId}
            label="Company"
            onChange={handleCompanyChange}
            error={mutation.isError && !selectedCompanyId}
          >
            <MenuItem value="" disabled>
              <em>Select a company</em>
            </MenuItem>
            {companies && companies.map((company) => (
              <MenuItem key={company.CompanyId} value={company.CompanyId.toString()}>
                {company.CompanyName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Show site group form only if a company is selected */}
      {selectedCompanyId && (
        <>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {mutation.error.message}
            </Alert>
          )}
          
          <SiteGroupForm 
            companyId={parseInt(selectedCompanyId)}
            onSubmit={handleSubmit} 
            isLoading={mutation.isPending}
          />
        </>
      )}

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SiteGroupCreatePage;